import {
  checklistPersonSchema,
  eventSpaceSchema,
  mediaVariant,
  ZONE_COLORS,
  ZONE_MIN_SIZE,
  zoneColorSchema,
  type AdminEvent,
  type ChecklistPerson,
  type EventSpace,
  type EventZone,
  type ZoneBox,
  type ZoneColor,
} from '@growup/site-shared';
import {
  Autocomplete,
  Button,
  Card,
  Checkbox,
  cx,
  Field,
  Input,
  Notice,
  Select,
  Textarea,
} from '@growup/ui';
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type FormEvent,
  type KeyboardEvent,
  type PointerEvent,
} from 'react';
import { z } from 'zod';

import { ApiError, getJson, messageOf, send } from '../api';
import { ImageField } from '../media/ImageUpload';

/** Sem planta, a grelha vazia tem este formato. */
const EMPTY_ASPECT = 16 / 10;
/** Passo das setas do teclado, em milesimas. */
const STEP = 10;

const clamp = (v: number, min: number, max: number) => Math.min(max, Math.max(min, v));

/** Mantem a zona dentro da planta e com o tamanho minimo. */
export function fitBox(box: ZoneBox): ZoneBox {
  const w = clamp(Math.round(box.w), ZONE_MIN_SIZE, 1000);
  const h = clamp(Math.round(box.h), ZONE_MIN_SIZE, 1000);
  return {
    x: clamp(Math.round(box.x), 0, 1000 - w),
    y: clamp(Math.round(box.y), 0, 1000 - h),
    w,
    h,
  };
}

const sameBox = (a: ZoneBox, b: ZoneBox) =>
  a.x === b.x && a.y === b.y && a.w === b.w && a.h === b.h;

const zoneBody = (z: EventZone, box: ZoneBox = z) => ({
  name: z.name,
  color: z.color,
  notes: z.notes,
  showOnSite: z.showOnSite,
  responsibleAccountIds: z.responsibles.map((p) => p.accountId),
  ...box,
});

const subtitle = (z: EventZone) =>
  [z.notes.split('\n')[0], z.responsibles.map((p) => p.login).join(', ')]
    .filter(Boolean)
    .join(' · ');

/**
 * Espaco do evento (docs/eventos, RF-E19 a RF-E21): a planta e as zonas, que
 * se arrastam e redimensionam por cima (ou com as setas, e Shift para o
 * tamanho). A pagina publica do evento mostra o nome e a cor das zonas com
 * "mostrar no site". Precisa de `events.manage`.
 */
export function SpaceView({ event, events }: { event: AdminEvent; events: AdminEvent[] }) {
  const base = `/api/site/events/${event.id}/space`;
  const [space, setSpace] = useState<EventSpace | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [planOpen, setPlanOpen] = useState(false);
  const [copying, setCopying] = useState(false);
  const [aspect, setAspect] = useState(EMPTY_ASPECT);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    getJson(base, eventSpaceSchema)
      .then(setSpace)
      .catch((e: unknown) => setError(messageOf(e)));
  }, [base]);
  useEffect(load, [load]);
  useEffect(() => {
    if (!space?.floorPlanUrl) setAspect(EMPTY_ASPECT);
  }, [space?.floorPlanUrl]);

  const run = async (action: () => Promise<unknown>): Promise<EventSpace | null> => {
    setBusy(true);
    setError(null);
    try {
      const next = eventSpaceSchema.parse(await action());
      setSpace(next);
      return next;
    } catch (e) {
      setError(messageOf(e));
      load();
      return null;
    } finally {
      setBusy(false);
    }
  };

  if (space === null) {
    return error ? <Notice tone="danger">{error}</Notice> : <p>A carregar…</p>;
  }

  const zone = space.zones.find((z) => z.id === selected) ?? null;
  const others = events.filter((e) => e.id !== event.id);

  const addZone = async () => {
    const before = new Set(space.zones.map((z) => z.id));
    const next = await run(() =>
      send('POST', `${base}/zones`, {
        name: 'Nova zona',
        color: zoneColorSchema.options[space.zones.length % zoneColorSchema.options.length],
        x: 400,
        y: 400,
        w: 200,
        h: 150,
      }),
    );
    setSelected(next?.zones.find((z) => !before.has(z.id))?.id ?? null);
  };

  const saveBox = (z: EventZone, box: ZoneBox) => {
    if (sameBox(z, box)) return;
    // Mostra ja onde ficou; a resposta da API confirma.
    setSpace({ ...space, zones: space.zones.map((x) => (x.id === z.id ? { ...x, ...box } : x)) });
    void run(() => send('PUT', `${base}/zones/${z.id}`, zoneBody(z, box)));
  };

  return (
    <div className="flex flex-col gap-4">
      <Card
        headingLevel={3}
        title="Espaço"
        actions={
          <div className="flex flex-wrap gap-2">
            {space.zones.length === 0 && others.length > 0 ? (
              <Button size="sm" variant="secondary" onClick={() => setCopying((c) => !c)}>
                Copiar de outro evento
              </Button>
            ) : null}
            <Button size="sm" variant="secondary" onClick={() => setPlanOpen((o) => !o)}>
              {space.floorPlanUrl ? 'Trocar planta' : 'Carregar planta'}
            </Button>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => printSpace(event.title, space, aspect)}
            >
              Imprimir
            </Button>
            <Button size="sm" disabled={busy} onClick={() => void addZone()}>
              Nova zona
            </Button>
          </div>
        }
      >
        <div className="flex flex-col gap-4">
          <p className="text-gu-ink-muted">
            {space.floorPlanUrl ? 'Planta carregada' : 'Sem planta: uma grelha vazia'} · arraste os
            retângulos para posicionar as zonas e o canto para mudar o tamanho.
          </p>
          {error ? <Notice tone="danger">{error}</Notice> : null}
          {planOpen ? (
            <div className="border-2 border-gu-line p-3">
              <ImageField
                label="Planta"
                optional
                value={space.floorPlanUrl}
                hint="PNG ou JPG; um PDF converte-se antes. Vai inteira, sem recorte. As zonas ficam no mesmo sítio relativo."
                onChange={(floorPlanUrl) =>
                  void run(() => send('PUT', `${base}/plan`, { floorPlanUrl })).then(() =>
                    setPlanOpen(false),
                  )
                }
              />
            </div>
          ) : null}
          {copying ? (
            <CopyFrom
              events={others}
              busy={busy}
              onCopy={(fromEventId) =>
                void run(() => send('POST', `${base}/copy`, { fromEventId })).then((next) => {
                  if (next) setCopying(false);
                })
              }
            />
          ) : null}
          <div className="grid gap-4 xl:grid-cols-[1fr_20rem]">
            <Plan
              space={space}
              aspect={aspect}
              onAspect={setAspect}
              selected={selected}
              onSelect={setSelected}
              onMove={saveBox}
            />
            <div className="flex flex-col gap-4">
              <section className="flex flex-col gap-1">
                <h4 className="font-sub text-label font-bold uppercase text-gu-ink-muted">Zonas</h4>
                {space.zones.length === 0 ? (
                  <p className="text-gu-ink-muted">Ainda não há zonas.</p>
                ) : (
                  <ul aria-label="Zonas">
                    {space.zones.map((z) => (
                      <li key={z.id} className="border-b border-gu-line">
                        <button
                          type="button"
                          aria-pressed={z.id === selected}
                          onClick={() => setSelected(z.id)}
                          className={cx(
                            'flex w-full items-center gap-2 py-2 text-left',
                            z.id === selected && 'font-semibold',
                          )}
                        >
                          <span
                            aria-hidden="true"
                            className="size-3 shrink-0 border-2"
                            style={{ borderColor: ZONE_COLORS[z.color].hex }}
                          />
                          <span className="flex-1 truncate">{z.name}</span>
                          {z.showOnSite ? null : (
                            <span className="text-caption text-gu-ink-muted">só staff</span>
                          )}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
              {zone ? (
                <ZoneEditor
                  key={zone.id}
                  base={base}
                  eventId={event.id}
                  zone={zone}
                  onSaved={setSpace}
                  onDeleted={(next) => {
                    setSpace(next);
                    setSelected(null);
                  }}
                />
              ) : space.zones.length > 0 ? (
                <p className="text-caption text-gu-ink-muted">
                  Escolha uma zona para lhe mudar o nome, a cor, os responsáveis e as notas de
                  material.
                </p>
              ) : null}
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}

type Drag = { id: string; mode: 'move' | 'resize'; startX: number; startY: number; box: ZoneBox };

function Plan({
  space,
  aspect,
  onAspect,
  selected,
  onSelect,
  onMove,
}: {
  space: EventSpace;
  aspect: number;
  onAspect: (aspect: number) => void;
  selected: string | null;
  onSelect: (id: string) => void;
  onMove: (zone: EventZone, box: ZoneBox) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [drag, setDrag] = useState<Drag | null>(null);
  const [draft, setDraft] = useState<{ id: string; box: ZoneBox } | null>(null);

  const boxOf = (z: EventZone): ZoneBox => (draft?.id === z.id ? draft.box : z);

  const start = (e: PointerEvent, z: EventZone, mode: Drag['mode']) => {
    e.stopPropagation();
    e.preventDefault();
    onSelect(z.id);
    (e.target as Element).setPointerCapture?.(e.pointerId);
    setDrag({
      id: z.id,
      mode,
      startX: e.clientX,
      startY: e.clientY,
      box: { x: z.x, y: z.y, w: z.w, h: z.h },
    });
  };

  const moveTo = (e: PointerEvent) => {
    if (!drag || !ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const dx = ((e.clientX - drag.startX) / rect.width) * 1000;
    const dy = ((e.clientY - drag.startY) / rect.height) * 1000;
    const b = drag.box;
    setDraft({
      id: drag.id,
      box:
        drag.mode === 'move'
          ? fitBox({ ...b, x: b.x + dx, y: b.y + dy })
          : fitBox({ ...b, w: Math.min(b.w + dx, 1000 - b.x), h: Math.min(b.h + dy, 1000 - b.y) }),
    });
  };

  const end = () => {
    const z = space.zones.find((x) => x.id === drag?.id);
    if (z && draft?.id === z.id) onMove(z, draft.box);
    setDrag(null);
    setDraft(null);
  };

  const onKey = (e: KeyboardEvent, z: EventZone) => {
    const delta: Record<string, [number, number]> = {
      ArrowLeft: [-STEP, 0],
      ArrowRight: [STEP, 0],
      ArrowUp: [0, -STEP],
      ArrowDown: [0, STEP],
    };
    const d = delta[e.key];
    if (!d) return;
    e.preventDefault();
    const b = boxOf(z);
    setDraft({
      id: z.id,
      box: e.shiftKey
        ? fitBox({ ...b, w: Math.min(b.w + d[0], 1000 - b.x), h: Math.min(b.h + d[1], 1000 - b.y) })
        : fitBox({ ...b, x: b.x + d[0], y: b.y + d[1] }),
    });
  };

  return (
    <div
      ref={ref}
      className="relative w-full touch-none select-none overflow-hidden border-2 border-gu-line bg-gu-sunken"
      style={{
        aspectRatio: String(aspect),
        backgroundImage:
          'linear-gradient(rgb(var(--gu-line) / 0.5) 1px, transparent 1px), linear-gradient(90deg, rgb(var(--gu-line) / 0.5) 1px, transparent 1px)',
        backgroundSize: '5% 8%',
      }}
      onPointerMove={moveTo}
      onPointerUp={end}
      onPointerCancel={end}
    >
      {space.floorPlanUrl ? (
        <img
          src={mediaVariant(space.floorPlanUrl, 'lg')}
          alt="Planta do espaço"
          className="pointer-events-none absolute inset-0 size-full object-fill"
          onLoad={(e) => {
            const img = e.currentTarget;
            if (img.naturalWidth > 0 && img.naturalHeight > 0) {
              onAspect(img.naturalWidth / img.naturalHeight);
            }
          }}
        />
      ) : null}
      {space.zones.map((z) => {
        const b = boxOf(z);
        const color = ZONE_COLORS[z.color].hex;
        return (
          <div
            key={z.id}
            role="button"
            tabIndex={0}
            aria-label={`Zona ${z.name}. Setas para mover, Shift e setas para o tamanho.`}
            aria-pressed={z.id === selected}
            onPointerDown={(e) => start(e, z, 'move')}
            onFocus={() => onSelect(z.id)}
            onKeyDown={(e) => onKey(e, z)}
            onKeyUp={() => {
              if (draft?.id === z.id) {
                onMove(z, draft.box);
                setDraft(null);
              }
            }}
            className={cx(
              'absolute cursor-move overflow-hidden border-2 p-1.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gu-focus',
              z.id === selected ? 'border-[3px]' : '',
            )}
            style={{
              left: `${String(b.x / 10)}%`,
              top: `${String(b.y / 10)}%`,
              width: `${String(b.w / 10)}%`,
              height: `${String(b.h / 10)}%`,
              borderColor: color,
              backgroundColor: `${color}1f`,
            }}
          >
            <span className="block truncate text-caption font-bold text-gu-ink">{z.name}</span>
            <span className="block truncate text-[0.7rem] text-gu-ink-muted">{subtitle(z)}</span>
            <span
              aria-hidden="true"
              onPointerDown={(e) => start(e, z, 'resize')}
              className="absolute bottom-0 right-0 size-3 cursor-nwse-resize"
              style={{ backgroundColor: color }}
            />
          </div>
        );
      })}
    </div>
  );
}

function ZoneEditor({
  base,
  eventId,
  zone,
  onSaved,
  onDeleted,
}: {
  base: string;
  eventId: string;
  zone: EventZone;
  onSaved: (space: EventSpace) => void;
  onDeleted: (space: EventSpace) => void;
}) {
  const [name, setName] = useState(zone.name);
  const [color, setColor] = useState<ZoneColor>(zone.color);
  const [notes, setNotes] = useState(zone.notes);
  const [showOnSite, setShowOnSite] = useState(zone.showOnSite);
  const [people, setPeople] = useState<ChecklistPerson[]>(zone.responsibles);
  const [query, setQuery] = useState('');
  const [fields, setFields] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Mesmas sugestoes que a checklist: voluntarios confirmados e contas de adulto.
  const search = (q: string) =>
    getJson(
      `/api/site/events/${eventId}/checklist/people?q=${encodeURIComponent(q)}`,
      z.array(checklistPersonSchema),
    );

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (name.trim() === '') {
      setFields({ name: 'Dê um nome à zona.' });
      return;
    }
    setBusy(true);
    setError(null);
    setFields({});
    try {
      const res = await send('PUT', `${base}/zones/${zone.id}`, {
        ...zoneBody({ ...zone, name, color, notes, showOnSite, responsibles: people }),
      });
      onSaved(eventSpaceSchema.parse(res));
    } catch (err) {
      if (err instanceof ApiError) setFields(err.fields);
      setError(messageOf(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Apagar a zona «${zone.name}»?`)) return;
    setBusy(true);
    try {
      onDeleted(eventSpaceSchema.parse(await send('DELETE', `${base}/zones/${zone.id}`)));
    } catch (err) {
      setError(messageOf(err));
      setBusy(false);
    }
  };

  return (
    <form
      aria-label="Zona selecionada"
      className="flex flex-col gap-3 border-2 border-gu-line p-3"
      onSubmit={(e) => void submit(e)}
      noValidate
    >
      <h4 className="font-sub text-label font-bold uppercase text-gu-ink-muted">
        Zona selecionada
      </h4>
      {error ? <Notice tone="danger">{error}</Notice> : null}
      <Field label="Nome" error={fields.name}>
        <Input value={name} maxLength={60} onChange={(e) => setName(e.target.value)} />
      </Field>
      <Field label="Cor">
        <Select value={color} onChange={(e) => setColor(e.target.value as ZoneColor)}>
          {zoneColorSchema.options.map((c) => (
            <option key={c} value={c}>
              {ZONE_COLORS[c].label}
            </option>
          ))}
        </Select>
      </Field>
      <Field
        label="Responsáveis"
        optional
        hint="Staff ou voluntários confirmados neste evento."
        error={fields.responsibleAccountIds}
      >
        <Autocomplete
          value={query}
          onChange={setQuery}
          search={search}
          getKey={(p) => p.accountId}
          renderItem={(p) => (
            <span className="flex min-w-0 flex-col">
              <span className="truncate font-semibold">{p.login}</span>
              {p.volunteer ? (
                <span className="text-caption text-gu-ink-muted">voluntário confirmado</span>
              ) : null}
            </span>
          )}
          onSelect={(p) => {
            setQuery('');
            setPeople((list) =>
              list.some((x) => x.accountId === p.accountId) ? list : [...list, p],
            );
          }}
          placeholder="Procurar pelo login…"
        />
      </Field>
      {people.length > 0 ? (
        <ul className="flex flex-wrap gap-2" aria-label="Responsáveis escolhidos">
          {people.map((p) => (
            <li key={p.accountId} className="flex items-center gap-1 border border-gu-line px-2">
              {p.login}
              <button
                type="button"
                aria-label={`Tirar ${p.login}`}
                className="text-gu-ink-muted hover:text-gu-ink"
                onClick={() => setPeople((list) => list.filter((x) => x.accountId !== p.accountId))}
              >
                ✕
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      <Field label="Notas de material" optional error={fields.notes}>
        <Textarea
          value={notes}
          rows={3}
          maxLength={1000}
          placeholder="40 cadeiras, 4 extensões"
          onChange={(e) => setNotes(e.target.value)}
        />
      </Field>
      <Checkbox
        label="Mostrar no site"
        hint="O nome e a cor aparecem na planta da página do evento. Nunca as pessoas nem as notas."
        checked={showOnSite}
        onChange={(e) => setShowOnSite(e.target.checked)}
      />
      <div className="flex flex-wrap gap-2">
        <Button type="submit" size="sm" disabled={busy}>
          Guardar zona
        </Button>
        <Button size="sm" variant="ghost" disabled={busy} onClick={() => void remove()}>
          Apagar
        </Button>
      </div>
    </form>
  );
}

function CopyFrom({
  events,
  busy,
  onCopy,
}: {
  events: AdminEvent[];
  busy: boolean;
  onCopy: (fromEventId: string) => void;
}) {
  const [from, setFrom] = useState(events[0]?.id ?? '');
  return (
    <form
      className="flex flex-wrap items-end gap-3 border-2 border-gu-line p-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (from) onCopy(from);
      }}
    >
      <Field
        label="Usar o espaço de"
        hint="Traz a planta e as zonas, sem responsáveis. Para um evento no mesmo sítio."
        className="min-w-64 flex-1"
      >
        <Select value={from} onChange={(e) => setFrom(e.target.value)}>
          {events.map((e) => (
            <option key={e.id} value={e.id}>
              {e.title}
            </option>
          ))}
        </Select>
      </Field>
      <Button type="submit" disabled={busy || !from}>
        Copiar
      </Button>
    </form>
  );
}

const escape = (text: string) => text.replace(/[&<>"']/g, (c) => `&#${String(c.charCodeAt(0))};`);

/** A planta com as zonas e a legenda numa folha ao baixo, para afixar no dia (RF-E21). */
export function printSpace(title: string, space: EventSpace, aspect: number): void {
  const win = window.open('', '_blank');
  if (!win) return;
  const plan = space.floorPlanUrl
    ? `<img src="${escape(new URL(mediaVariant(space.floorPlanUrl, 'lg'), window.location.origin).toString())}" alt="">`
    : '';
  const zones = space.zones
    .map((z) => {
      const hex = ZONE_COLORS[z.color].hex;
      return `<div class="zone" style="left:${String(z.x / 10)}%;top:${String(z.y / 10)}%;width:${String(z.w / 10)}%;height:${String(z.h / 10)}%;border-color:${hex};background:${hex}22"><b>${escape(z.name)}</b></div>`;
    })
    .join('');
  const legend = space.zones
    .map(
      (z) =>
        `<li><span class="dot" style="border-color:${ZONE_COLORS[z.color].hex}"></span><b>${escape(z.name)}</b>${
          z.responsibles.length > 0
            ? ` · ${escape(z.responsibles.map((p) => p.login).join(', '))}`
            : ''
        }${z.notes ? `<br><small>${escape(z.notes)}</small>` : ''}</li>`,
    )
    .join('');
  win.document
    .write(`<!doctype html><html lang="pt"><head><meta charset="utf-8"><title>${escape(title)} · Espaço</title>
<style>
@page { size: A4 landscape; margin: 10mm; }
body { font-family: system-ui, sans-serif; margin: 0; color: #111; }
h1 { font-size: 16pt; margin: 0 0 4mm; text-transform: uppercase; }
.sheet { display: flex; gap: 6mm; align-items: flex-start; }
.plan { position: relative; flex: 1; aspect-ratio: ${String(aspect)}; border: 1px solid #999;
  background-image: linear-gradient(#ddd 1px, transparent 1px), linear-gradient(90deg, #ddd 1px, transparent 1px);
  background-size: 5% 8%; }
.plan img { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: fill; }
.zone { position: absolute; border: 2px solid; padding: 1mm; font-size: 9pt; overflow: hidden; box-sizing: border-box; }
ul { list-style: none; padding: 0; margin: 0; width: 60mm; font-size: 9pt; }
li { margin-bottom: 2mm; }
.dot { display: inline-block; width: 3mm; height: 3mm; border: 2px solid; margin-right: 2mm; vertical-align: middle; }
* { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
</style></head><body><h1>${escape(title)}</h1><div class="sheet"><div class="plan">${plan}${zones}</div><ul>${legend}</ul></div>
<script>window.onload = function () { window.print(); };</script></body></html>`);
  win.document.close();
}
